import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ROUTES } from '../utils/constants';
import { FaUser, FaEnvelope, FaLock, FaSave } from 'react-icons/fa';
import apiService from '../services/api';

const ProfilePage = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
      return;
    }
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || ''
      }));
      loadUnreadCount();
    }
  }, [user, loading, navigate]);

  const loadUnreadCount = async () => {
    try {
      const readNotificationIds = JSON.parse(localStorage.getItem('readNotifications') || '[]');
      const generatedNotifications = [];
      
      const purchases = await apiService.getUserPurchases();
      purchases.forEach((purchase) => {
        const notifId = `purchase-${purchase.id}`;
        generatedNotifications.push({
          id: notifId,
          read: readNotificationIds.includes(notifId)
        });
      });

      try {
        const payments = await apiService.getUserManualPayments();
        payments.forEach((payment) => {
          if (payment.status === 'approved' || payment.status === 'rejected') {
            const notifId = `payment-${payment.status}-${payment.id}`;
            generatedNotifications.push({
              id: notifId,
              read: readNotificationIds.includes(notifId)
            });
          }
        });
      } catch (err) {
        console.error('Error loading payments for notifications:', err);
      }

      const unread = generatedNotifications.filter(n => !n.read).length;
      setUnreadCount(unread);
    } catch (err) {
      console.error('Error loading unread count:', err);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (formData.newPassword && formData.newPassword !== formData.confirmPassword) {
      setError('كلمات المرور غير متطابقة');
      return;
    }

    if (formData.newPassword && formData.newPassword.length < 6) {
      setError('كلمة المرور الجديدة يجب أن تكون 6 أحرف على الأقل');
      return;
    }

    setUpdating(true);

    // TODO: Implement API call to update profile
    setTimeout(() => {
      setSuccess('تم تحديث الملف الشخصي بنجاح!');
      setUpdating(false);
      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
    }, 1000);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg dark:bg-gray-900">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text3 dark:text-gray-400">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="relative z-10 py-12 md:py-20 pb-24 md:pb-20 bg-gradient-to-b from-bg to-white dark:from-gray-900 dark:to-gray-800 min-h-screen" dir="rtl">
      <div className="container mx-auto px-3 md:px-4">
        <div className="max-w-[800px] mx-auto">
          {/* Back Button */}
          <Link
            to={ROUTES.DASHBOARD}
            className="inline-flex items-center gap-2 text-primary hover:underline font-bold mb-4 text-sm md:text-base"
          >
            <i className="fas fa-arrow-right"></i>
            العودة للوحة التحكم
          </Link>

          {/* Header */}
          <div className="text-center mb-6 md:mb-8">
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-black text-text dark:text-white mb-2">
              الملف الشخصي
            </h1>
            <p className="text-sm md:text-lg text-text3 dark:text-gray-400">
              قم بتحديث معلوماتك الشخصية وإعداداتك
            </p>
          </div>

          {/* Profile Card */}
          <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-custom-lg p-8 mb-6">
            {error && (
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800 rounded-xl text-red-600 dark:text-red-400 text-center">
                {error}
              </div>
            )}

            {success && (
              <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border-2 border-green-200 dark:border-green-800 rounded-xl text-green-600 dark:text-green-400 text-center">
                {success}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div>
                <label className="block text-text dark:text-white font-bold mb-2 flex items-center gap-2">
                  <FaUser className="text-primary" />
                  الاسم الكامل
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-5 py-4 bg-bg dark:bg-gray-700 border-2 border-border dark:border-gray-600 rounded-xl text-text dark:text-white outline-none focus:border-primary transition-colors"
                  placeholder="دخل اسمك الكامل"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-text dark:text-white font-bold mb-2 flex items-center gap-2">
                  <FaEnvelope className="text-primary" />
                  البريد الإلكتروني
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled
                  className="w-full px-5 py-4 bg-gray-100 dark:bg-gray-600 border-2 border-border dark:border-gray-500 rounded-xl text-text dark:text-gray-300 outline-none cursor-not-allowed"
                  placeholder="example@email.com"
                />
                <p className="text-sm text-text3 dark:text-gray-400 mt-2">
                  لا يمكن تغيير البريد الإلكتروني
                </p>
              </div>

              {/* Change Password Section */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <h3 className="text-xl font-bold text-text dark:text-white mb-4 flex items-center gap-2">
                  <FaLock className="text-primary" />
                  تغيير كلمة المرور
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-text dark:text-white font-bold mb-2">
                      كلمة المرور الحالية
                    </label>
                    <input
                      type="password"
                      name="currentPassword"
                      value={formData.currentPassword}
                      onChange={handleChange}
                      className="w-full px-5 py-4 bg-bg dark:bg-gray-700 border-2 border-border dark:border-gray-600 rounded-xl text-text dark:text-white outline-none focus:border-primary transition-colors"
                      placeholder="••••••••"
                    />
                  </div>

                  <div>
                    <label className="block text-text dark:text-white font-bold mb-2">
                      كلمة المرور الجديدة
                    </label>
                    <input
                      type="password"
                      name="newPassword"
                      value={formData.newPassword}
                      onChange={handleChange}
                      className="w-full px-5 py-4 bg-bg dark:bg-gray-700 border-2 border-border dark:border-gray-600 rounded-xl text-text dark:text-white outline-none focus:border-primary transition-colors"
                      placeholder="••••••••"
                    />
                  </div>

                  <div>
                    <label className="block text-text dark:text-white font-bold mb-2">
                      تأكيد كلمة المرور الجديدة
                    </label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className="w-full px-5 py-4 bg-bg dark:bg-gray-700 border-2 border-border dark:border-gray-600 rounded-xl text-text dark:text-white outline-none focus:border-primary transition-colors"
                      placeholder="••••••••"
                    />
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={updating}
                className="w-full px-8 py-4 bg-primary text-white font-bold rounded-xl hover:bg-primary-dark transition-all shadow-custom hover:shadow-custom-lg disabled:opacity-50 flex items-center justify-center gap-3"
              >
                <FaSave />
                {updating ? 'جاري الحفظ...' : 'حفظ التغييرات'}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Fixed Bottom Navigation - Mobile Only */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-2xl z-50 safe-area-bottom">
        <div className="grid grid-cols-3 gap-1 px-2 py-1">
          <Link
            to="/notifications"
            className="flex flex-col items-center justify-center py-1.5 px-2 rounded-lg hover:bg-gray-100 transition-colors relative"
          >
            <i className="fas fa-bell text-lg text-text2 mb-0.5"></i>
            <span className="text-[9px] font-bold text-text2">الإشعارات</span>
            {unreadCount > 0 && (
              <span className="absolute top-0.5 right-0.5 w-4 h-4 bg-red-500 text-white text-[8px] font-bold rounded-full flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </Link>
          
          <Link
            to={ROUTES.DASHBOARD}
            className="flex flex-col items-center justify-center py-1.5 px-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <i className="fas fa-home text-lg text-text2 mb-0.5"></i>
            <span className="text-[9px] font-bold text-text2">لوحة التحكم</span>
          </Link>
          
          <Link
            to="/profile"
            className="flex flex-col items-center justify-center py-1.5 px-2 rounded-lg bg-primary text-white transition-colors"
          >
            <i className="fas fa-user text-lg mb-0.5"></i>
            <span className="text-[9px] font-bold">الملف الشخصي</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
